/**
 * NPC loot timers (website roadmap S05; one copy for the server, the website and the extension).
 *
 * Torn's loot NPCs drop better loot the longer they've been out of hospital: level I when they leave, then II–V after
 * 30 min, 1½ h, 3½ h and 7½ h. The Omni-Torn server reads each NPC's PUBLIC profile status with its own key
 * (apps/api/src/npc-loot.ts, about one call a minute in all) and serves the times at `GET /api/npc/loot`. NPCs aren't
 * players: nothing about any player is read or kept.
 */

/** The loot NPCs the server follows, in Torn's id order. */
export const LOOT_NPCS: ReadonlyArray<{ id: number; name: string }> = [
  { id: 4, name: 'Duke' },
  { id: 7, name: 'Amanda' },
  { id: 10, name: 'Scrooge' },
  { id: 15, name: 'Leslie' },
  { id: 19, name: 'Jimmy' },
  { id: 20, name: 'Fernando' },
  { id: 21, name: 'Tiny' },
];

/** Minutes after leaving hospital each loot level starts (I–V). */
export const LOOT_LEVEL_MINUTES: readonly number[] = [0, 30, 90, 210, 450];
const LEVEL_AFTER = LOOT_LEVEL_MINUTES.map((m) => m * 60_000);
const ROMAN = ['I', 'II', 'III', 'IV', 'V'];

/** 1 → "I" … 5 → "V" ("" for anything else). */
export const lootRoman = (level: number): string => ROMAN[level - 1] ?? '';

/** When each level (I–V) starts, from the moment the NPC leaves hospital (ms). */
export function lootLevelTimes(hospOut: number): number[] {
  return LEVEL_AFTER.map((d) => hospOut + d);
}

/**
 * The loot level at `now` (0 = still in hospital, or unknown) and when the next one starts (null at V or unknown).
 */
export function lootLevel(n: { hospOut: number | null }, now: number): { level: number; nextAt: number | null } {
  if (n.hospOut === null) return { level: 0, nextAt: null };
  const since = now - n.hospOut;
  if (since < 0) return { level: 0, nextAt: n.hospOut };
  let level = 1;
  while (level < 5 && since >= LEVEL_AFTER[level]!) level++;
  return { level, nextAt: level < 5 ? n.hospOut + LEVEL_AFTER[level]! : null };
}

/** One NPC as the server last saw it. */
export interface NpcLootTimer {
  id: number;
  name: string;
  /** Torn's status: in hospital, okay (out), something else (travelling, jail, federal…), or not checked yet. */
  state: 'hospital' | 'okay' | 'other' | 'unknown';
  /** Torn's own words for the status ("In hospital for 2 hrs 3 mins"), when known. */
  description: string | null;
  /**
   * When it left (or leaves) hospital (ms) — the start of loot level I. Null when not known: the server has only seen
   * it out of hospital so far (it learns the time the next time it sees the NPC in hospital).
   */
  hospOut: number | null;
  /** When the server last read its profile (ms), null if never. */
  checkedAt: number | null;
}

/** `GET /api/npc/loot`. */
export interface NpcLootResponse {
  npcs: NpcLootTimer[];
  /** The server's clock when it answered (ms) — the page corrects its own clock by it. */
  now: number;
  /** False when the server has no key of its own to read the NPCs with (nothing is updated then). */
  polling: boolean;
}

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const ms = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) && x > 0 ? Math.round(x) : null);
const STATES = new Set(['hospital', 'okay', 'other', 'unknown']);

/** The server's answer, checked (the website reads it through this). Null when it isn't one. */
export function readNpcLoot(body: unknown): NpcLootResponse | null {
  const b = obj(body);
  if (!b || !Array.isArray(b.npcs)) return null;
  const npcs: NpcLootTimer[] = [];
  for (const raw of b.npcs.slice(0, 50)) {
    const n = obj(raw);
    const id = Number(n?.id);
    if (!n || !Number.isInteger(id) || id <= 0) continue;
    npcs.push({
      id,
      name: typeof n.name === 'string' && n.name.length <= 40 ? n.name : `NPC ${id}`,
      state: typeof n.state === 'string' && STATES.has(n.state) ? (n.state as NpcLootTimer['state']) : 'unknown',
      description: typeof n.description === 'string' ? n.description.slice(0, 120) : null,
      hospOut: ms(n.hospOut),
      checkedAt: ms(n.checkedAt),
    });
  }
  return { npcs, now: ms(b.now) ?? Date.now(), polling: b.polling === true };
}

/**
 * Torn's v2 `/user/{id}/basic` (or `/profile`) answer → the NPC's state and, when in hospital, when it leaves (ms).
 * Null when the answer isn't a profile.
 */
export function readNpcStatus(body: unknown): { state: NpcLootTimer['state']; description: string | null; until: number | null } | null {
  const b = obj(body);
  const p = obj(b?.profile) ?? b;
  const st = obj(p?.status);
  if (!st || typeof st.state !== 'string') return null;
  const s = st.state.toLowerCase();
  const until = typeof st.until === 'number' && Number.isFinite(st.until) && st.until > 0 ? st.until * 1000 : null;
  return {
    state: s === 'hospital' ? 'hospital' : s === 'okay' ? 'okay' : 'other',
    description: typeof st.description === 'string' ? st.description.slice(0, 120) : null,
    until,
  };
}

/**
 * Loot Rangers' (lzpt.io) attack plan as the extension hands it to the website (`npc` through the site bridge: the
 * extension's own kept copy, apps/extension bg/npc-loot.ts). Shown beside the server's times; never sent to or kept by
 * the Omni-Torn server.
 */
export interface LootRangersPlan {
  /** The attack time Loot Rangers set (ms), null when none. */
  attackAt: number | null;
  attackNow: boolean;
  reason: string | null;
  /** The NPCs in the attack, in Loot Rangers' order. */
  order: Array<{ id: number; name: string }>;
  /** When the extension got it (ms). */
  at: number | null;
}

/** The extension's answer (`{ plan, problem, at }`) → the plan, or null (no extension, no plan, or not one). */
export function readLootRangersReply(x: unknown): LootRangersPlan | null {
  const r = obj(x);
  const plan = obj(r?.plan);
  if (!r || !plan || !Array.isArray(plan.npcs)) return null;
  const order: LootRangersPlan['order'] = [];
  for (const raw of plan.npcs.slice(0, 20)) {
    const n = obj(raw);
    const id = Number(n?.id);
    if (n && Number.isInteger(id) && id > 0) order.push({ id, name: typeof n.name === 'string' && n.name.length <= 40 ? n.name : `NPC ${id}` });
  }
  return {
    attackAt: ms(plan.attackAt),
    attackNow: plan.attackNow === true,
    reason: typeof plan.reason === 'string' && plan.reason.trim() ? plan.reason.trim().slice(0, 200) : null,
    order,
    at: ms(r.at),
  };
}
